import { renderTile } from '../ui/render.js';
import { makeTile } from '../engine/tiles.js';
import { SUITS, SUIT_NAMES, WINDS, DRAGONS } from '../engine/constants.js';

// Tutorial content lifted 1:1 from the original monolith. Each entry is a
// function so we can regenerate the tile HTML on each render.
const TUT = [
  () => {
    let h = '<h2>The Tiles</h2><p>American Mahjong uses <b>152 tiles</b>. Let\'s learn them!</p>';
    h += '<h3>Suited Tiles (3 suits × 9 numbers × 4 each = 108)</h3>';
    for (const s of SUITS) {
      const color = s === 'crak' ? 'red' : s === 'bam' ? 'green' : 'blue';
      const label = s === 'crak' ? '(Characters 万)' : s === 'bam' ? '(Bamboo 竹)' : '(Dots ●)';
      h += `<p style="font-weight:700;color:var(--${color})">${SUIT_NAMES[s]}s ${label}</p><div class="tut-tiles-row">`;
      for (let n = 1; n <= 9; n++) h += renderTile(makeTile('suited', s, n));
      h += '</div>';
    }
    h += '<h3>Wind Tiles (4 types × 4 each = 16)</h3><div class="tut-tiles-row">';
    for (const w of WINDS) h += renderTile(makeTile('wind', null, 0, w));
    h += '</div><h3>Dragon Tiles (3 types × 4 each = 12)</h3><div class="tut-tiles-row">';
    for (const d of DRAGONS) h += renderTile(makeTile('dragon', null, 0, d));
    h += '</div><h3>Flowers (8 total)</h3><p>Flowers are special — used in many hand patterns.</p><div class="tut-tiles-row">';
    for (let i = 0; i < 3; i++) h += renderTile(makeTile('flower'));
    h += '</div><h3>Jokers (8 total)</h3><p>Wild cards! They can substitute for any tile in groups of 3 or more. They <b>cannot</b> be used in pairs or as singles.</p><div class="tut-tiles-row">';
    for (let i = 0; i < 3; i++) h += renderTile(makeTile('joker'));
    h += '</div>';
    return h;
  },
  () =>
    '<h2>How a Game Works</h2>' +
    '<p><b>4 players</b> sit around the table, designated East, South, West, and North.</p>' +
    '<h3>The Deal</h3><p>All 152 tiles are shuffled and stacked face-down in a "wall." East deals 14 tiles to herself and 13 to each other player.</p>' +
    '<h3>The Charleston</h3><p>Before play begins, everyone passes unwanted tiles to other players in a specific pattern. This is unique to American Mahjong! (More on this in a later section.)</p>' +
    '<h3>A Turn</h3>' +
    '<div class="tut-callout"><strong>1.</strong> Draw a tile from the wall (or claim the last discard)<br><strong>2.</strong> Look at your hand — are you closer to completing a pattern?<br><strong>3.</strong> Discard one tile face-up to the center</div>' +
    '<h3>Claiming a Discard</h3><p>When someone discards a tile you need, you can <b>claim</b> it — but you must reveal (expose) the completed group face-up. Play then continues from your seat.</p>' +
    '<h3>The Goal</h3><p>Be the first to complete a valid hand pattern from the card. When you do, call <b>"Mahjong!"</b></p>',
  () =>
    '<h2>Key Terms</h2>' +
    '<div class="tut-term"><b>Pair</b><span>2 identical tiles. Example: two 5 Dots.</span></div>' +
    '<div class="tut-term"><b>Pung</b><span>3 identical tiles. Example: three East Winds.</span></div>' +
    '<div class="tut-term"><b>Kong</b><span>4 identical tiles. Example: four 8 Craks.</span></div>' +
    '<div class="tut-term"><b>Quint</b><span>5 identical tiles. Only possible with Jokers (there are only 4 of each tile, so the 5th must be a Joker).</span></div>' +
    '<div class="tut-term"><b>Sextet</b><span>6 identical tiles. Needs at least 2 Jokers.</span></div>' +
    '<div class="tut-term"><b>Exposure</b><span>A group placed face-up on the table after claiming a discard. Everyone can see your exposures.</span></div>' +
    '<div class="tut-term"><b>Concealed</b><span>A hand played entirely from your own draws — no exposures. Some patterns require this and are worth more points.</span></div>' +
    '<div class="tut-callout"><strong>Joker Rule:</strong> Jokers are wild in groups of 3+ (Pung, Kong, Quint, Sextet). They <b>cannot</b> be used in pairs or singles. A Joker in an exposure can be swapped by any player who has the real tile.</div>',
  () =>
    '<h2>The NMJL Card</h2>' +
    '<p>American Mahjong is unique because you play with a <b>yearly card</b> published by the National Mah Jongg League (NMJL). The card lists all the valid hand patterns for the year.</p>' +
    '<h3>How to Read the Card</h3>' +
    '<p>Each line on the card shows a specific combination of tiles needed to win. It uses abbreviations:</p>' +
    '<div class="tut-callout"><strong>F</strong> = Flower &nbsp; <strong>D</strong> = Dragon &nbsp; <strong>N/E/W/S</strong> = Wind<br>' +
    'Numbers represent tile values. The suit color on the card tells you which suits to use.<br>' +
    '<strong>Same color</strong> = same suit &nbsp; <strong>Different colors</strong> = different suits</div>' +
    '<p>For example, a line showing <code>FF 111 222 333 444</code> in one color means: a pair of Flowers plus four Pungs of consecutive numbers, all in the same suit.</p>' +
    '<h3>Categories</h3><p>Hands are grouped into categories like "2468" (even numbers), "Like Numbers," "Winds/Dragons," "Singles &amp; Pairs," and more. The card changes every year, so players buy a new one each April.</p>' +
    '<div class="tut-callout"><strong>Tonight\'s tip:</strong> Don\'t try to memorize the whole card. Pick 2-3 hands that seem simple and aim for those. You\'ll have the card in front of you at the table!</div>',
  () =>
    '<h2>The Charleston</h2>' +
    '<p>The Charleston is a tile-passing ritual at the start of the game. It lets you get rid of tiles you don\'t want and hopefully receive better ones.</p>' +
    '<h3>First Charleston (Mandatory)</h3>' +
    '<div class="tut-callout"><strong>Step 1:</strong> Pass 3 tiles to your <b>RIGHT</b> →<br><strong>Step 2:</strong> Pass 3 tiles <b>ACROSS</b> ↕<br><strong>Step 3:</strong> Pass 3 tiles to your <b>LEFT</b> ←</div>' +
    '<h3>Second Charleston (Optional — but usually done)</h3>' +
    '<div class="tut-callout"><strong>Step 4:</strong> Pass 3 tiles to your <b>LEFT</b> ←<br><strong>Step 5:</strong> Pass 3 tiles <b>ACROSS</b> ↕<br><strong>Step 6:</strong> Pass 3 tiles to your <b>RIGHT</b> →</div>' +
    '<h3>Courtesy Pass</h3><p>After both Charlestons, you may do a <b>Courtesy pass</b>: trade 0-3 tiles with the player across from you (both must agree on the number).</p>' +
    '<div class="tut-callout"><strong>Strategy tip:</strong> Pass tiles from suits you don\'t need. Look at your hand first and decide which 1-2 suits to keep. Pass everything else!</div>',
  () =>
    '<h2>Winning — Declaring Mahjong</h2>' +
    '<p>You win by completing one of the hand patterns on the card. Your hand must match <b>exactly</b> — right tiles, right groups, right number.</p>' +
    '<h3>Two Ways to Win</h3>' +
    '<div class="tut-callout"><strong>Self-picked:</strong> You draw the winning tile yourself from the wall.<br><strong>Claiming:</strong> Someone discards the tile you need to complete your hand.</div>' +
    '<h3>Calling Mahjong</h3><p>When you have a complete hand, call "Mahjong!" immediately. If you\'re claiming a discard, your Mahjong call <b>takes priority</b> over any other claim (Pung, Kong, etc.).</p>' +
    '<h3>Scoring</h3><p>Different hands are worth different points (typically 25 or 30, with concealed hands worth more). If nobody completes a hand and the wall runs out of tiles, it\'s a "wall game" — nobody wins that round.</p>' +
    '<div class="tut-callout"><strong>Don\'t be afraid to declare!</strong> Even experienced players sometimes misread the card. If you think you have it, go for it! Your tablemates will help verify.</div>',
  () =>
    '<h2>Tips for Tonight</h2>' +
    '<h3>Before the Game</h3>' +
    '<ul><li>Look at the card as soon as you get it. Pick 3-4 hands that look simple.</li>' +
    '<li>Organize your hand by suit — it helps you see patterns.</li>' +
    '<li>Don\'t worry about memorizing everything. Everyone looks at the card constantly!</li></ul>' +
    '<h3>During the Game</h3>' +
    '<ul><li>Be flexible — be willing to change your target hand based on what you draw.</li>' +
    '<li>Watch what others discard — it tells you what they DON\'T need.</li>' +
    '<li>Don\'t be afraid to ask questions — Mahjong players love teaching new players.</li>' +
    '<li>Jokers are gold. Keep them unless you\'re absolutely sure you don\'t need them.</li></ul>' +
    '<h3>Etiquette</h3>' +
    '<ul><li>Rack your tiles neatly so others can\'t see them.</li>' +
    '<li>Name your discard when you place it (e.g., "3 Bam").</li>' +
    '<li>Give others a moment to claim before the next person draws.</li>' +
    '<li>Have fun! It\'s your first time — nobody expects perfection.</li></ul>' +
    '<div class="tut-callout"><strong>Ready?</strong> Try a practice game before tonight! The Practice Game mode will walk you through everything.</div>',
];

let tutIdx = 0;

export function showTutorial(onDone) {
  tutIdx = 0;
  renderTut(onDone);
}

function renderTut(onDone) {
  document.getElementById('tutBody').innerHTML = TUT[tutIdx]();
  document.getElementById('tutStep').textContent = `${tutIdx + 1} / ${TUT.length}`;
  let dots = '';
  for (let i = 0; i < TUT.length; i++) {
    dots += `<div class="tut-dot${i < tutIdx ? ' done' : ''}${i === tutIdx ? ' active' : ''}"></div>`;
  }
  document.getElementById('tutDots').innerHTML = dots;
  document.getElementById('tutPrev').disabled = tutIdx === 0;
  document.getElementById('tutNext').textContent = tutIdx === TUT.length - 1 ? 'Back to Menu' : 'Next →';
  document.getElementById('tutBody').scrollTop = 0;

  document.getElementById('tutPrev').onclick = () => {
    tutIdx = Math.max(0, tutIdx - 1);
    renderTut(onDone);
  };
  document.getElementById('tutNext').onclick = () => {
    if (tutIdx === TUT.length - 1) {
      onDone();
      return;
    }
    tutIdx = Math.min(TUT.length - 1, tutIdx + 1);
    renderTut(onDone);
  };
}
